import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { ROLES } from "../lib/labels";

const Card = ({ title, children, testid }) => (
  <div className="container-vp py-14 flex justify-center" data-testid={testid}>
    <div className="w-full max-w-md card-vp p-8"><Logo /><h1 className="text-2xl font-extrabold text-[#153C3A] mt-6">{title}</h1>{children}</div>
  </div>
);
const Input = ({ id, label, ...p }) => (
  <div><label htmlFor={id} className="block text-xs font-semibold text-[#52706D] mb-1.5">{label}</label><input id={id} className="field-vp" required {...p} /></div>
);
const Err = ({ msg, testid }) => msg ? <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" role="alert" data-testid={testid}>{msg}</p> : null;

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const r = await api.post("/auth/forgot-password", { email }); setDone(r.data.message); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card title="Glemt adgangskode" testid="forgot-password-page">
      {done ? <p className="mt-4 text-sm text-[#153C3A] bg-[#E6F4EF] rounded-lg p-3" data-testid="forgot-password-done">{done}</p> : (
        <form onSubmit={submit} className="grid gap-4 mt-4">
          <p className="text-sm text-[#52706D]">Angiv din e-mail, så sender vi et link til at vælge en ny adgangskode. Linket gælder i 1 time.</p>
          <Input id="fp-email" label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email" autoComplete="email" />
          <Err msg={error} testid="forgot-password-error" />
          <button className="btn-primary w-full" disabled={busy} data-testid="forgot-password-submit">{busy ? "Sender…" : "Send link"}</button>
        </form>
      )}
      <Link to="/login" className="text-sm text-[#087F72] hover:underline mt-4 inline-block" data-testid="forgot-password-back">Tilbage til log ind</Link>
    </Card>
  );
};

export const ResetPassword = () => {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.a !== pw.b) return setError("Adgangskoderne er ikke ens");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token: sp.get("token") || "", password: pw.a }); setDone(true); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card title="Vælg ny adgangskode" testid="reset-password-page">
      {done ? <p className="mt-4 text-sm bg-[#E6F4EF] rounded-lg p-3" data-testid="reset-password-done">Din adgangskode er ændret, og alle tidligere sessioner er logget ud. <Link to="/login" className="text-[#087F72] underline">Log ind</Link></p> : (
        <form onSubmit={submit} className="grid gap-4 mt-4">
          <Input id="rp-a" label="Ny adgangskode" type="password" minLength={10} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="reset-password-input" autoComplete="new-password" />
          <Input id="rp-b" label="Gentag adgangskode" type="password" minLength={10} value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="reset-password-confirm" autoComplete="new-password" />
          <p className="text-xs text-[#52706D] -mt-2">Mindst 10 tegn med både bogstaver og tal.</p>
          <Err msg={error} testid="reset-password-error" />
          <button className="btn-primary w-full" disabled={busy} data-testid="reset-password-submit">{busy ? "Gemmer…" : "Gem ny adgangskode"}</button>
        </form>
      )}
    </Card>
  );
};

export const AcceptInvitation = () => {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [v, setV] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post("/invitations/info", { token }).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);
  const accept = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      await api.post("/invitations/accept", info.has_account ? { token } : { token, ...v });
      await refresh(); nav("/klinik/dashboard");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  if (!info) return <Card title="Invitation" testid="invitation-page">{error ? <div className="mt-4"><Err msg={error} testid="invitation-error" /></div> : <p className="mt-4 text-sm">Indlæser…</p>}</Card>;
  const wrongUser = info.has_account && (!user || user.email !== info.email);
  return (
    <Card title="Invitation til klinikportalen" testid="invitation-page">
      <p className="text-sm text-[#52706D] mt-3" data-testid="invitation-details">Du er inviteret til <strong>{info.clinic_name}</strong> som <strong>{ROLES[info.role]}</strong> ({info.email}).</p>
      <form onSubmit={accept} className="grid gap-4 mt-5">
        {!info.has_account && <>
          <Input id="inv-name" label="Fulde navn" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="invitation-name" />
          <Input id="inv-pw" label="Vælg adgangskode" type="password" minLength={10} value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} data-testid="invitation-password" autoComplete="new-password" />
        </>}
        {wrongUser && <p className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="invitation-login-required">Log ind med {info.email} for at acceptere. <Link className="text-[#087F72] underline" to={`/login?next=${encodeURIComponent(`/invitation?token=${token}`)}`}>Log ind</Link></p>}
        <Err msg={error} testid="invitation-error" />
        <button className="btn-primary w-full" disabled={busy || wrongUser} data-testid="invitation-accept">{busy ? "Vent…" : "Acceptér invitation"}</button>
      </form>
    </Card>
  );
};
